import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { initSounds, playSoundIfEnabled } from '../utils/soundEffects';
import SoundToggle from './SoundToggle';

const { FiArrowLeft, FiRefreshCw, FiDice, FiInfo, FiHelpCircle } = FiIcons;

const DiceGame = () => {
  const navigate = useNavigate();
  const [rolling, setRolling] = useState(false);
  const [dice1, setDice1] = useState(1);
  const [dice2, setDice2] = useState(6);
  const [challenge, setChallenge] = useState(null);
  const [gameLevel, setGameLevel] = useState('mild'); // 'mild', 'hot', 'extreme'
  const [showLevelSelector, setShowLevelSelector] = useState(true);
  const [betType, setBetType] = useState(null); // 'tai' or 'xiu'
  const [result, setResult] = useState(null); // 'win' or 'lose'
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Initialize sounds when component mounts
  useEffect(() => {
    initSounds();
  }, []);

  const challenges = {
    mild: [
      "Hôn người kia trong 10 giây",
      "Massage vai cho người kia trong 2 phút",
      "Ôm người kia thật chặt và thì thầm lời ngọt ngào",
      "Hôn cổ người kia trong 30 giây",
      "Nhảy sexy cho người kia xem trong 1 phút",
      "Kể cho người kia nghe điều bạn thích nhất về họ",
      "Hát một bài tình ca cho người kia nghe",
      "Mát xa lưng cho người kia trong 3 phút",
      "Cởi một món đồ của người kia",
      "Hôn khắp khuôn mặt người kia",
      "Mô tả điều bạn thích nhất về cơ thể người kia",
      "Cùng nhau uống một ly rượu/nước",
      "Kể một kỉ niệm đáng nhớ giữa hai người",
      "Nói ba điều bạn muốn làm cùng người kia trong tương lai",
      "Tự chụp một bức ảnh đôi thật đáng yêu",
      // Thêm 20 yêu cầu mới cho mức độ nhẹ nhàng
      "Vẽ một trái tim lên da của người kia bằng son môi",
      "Tự làm một loại cocktail đơn giản và cùng nhau thưởng thức",
      "Nhảy một điệu nhảy chậm lãng mạn trong 2 phút",
      "Bịt mắt người kia và cho họ nếm thử một món ăn",
      "Kể về lần đầu tiên bạn nhận ra mình có cảm xúc với người kia",
      "Mô tả chi tiết buổi hẹn hò trong mơ của bạn với người kia",
      "Dùng đá lạnh vẽ một hình trái tim lên da người kia",
      "Nói 'Anh/Em yêu em/anh' bằng 3 ngôn ngữ khác nhau",
      "Mô tả 3 điều khiến bạn bị hấp dẫn bởi người kia ngay từ cái nhìn đầu tiên",
      "Hôn lên mỗi ngón tay của người kia",
      "Vuốt ve tóc và mát-xa da đầu cho người kia trong 2 phút",
      "Nhắm mắt và để người kia hôn bạn ở bất kỳ đâu họ muốn",
      "Cùng nhau đọc một đoạn thơ tình yêu",
      "Hát karaoke một bài hát tình yêu cùng nhau",
      "Kể về giấc mơ lãng mạn nhất bạn từng có về người kia",
      "Thì thầm vào tai người kia 3 điều bạn muốn làm với họ tối nay",
      "Cho người kia một món quà nhỏ bạn đã chuẩn bị trước (hoặc tự làm)",
      "Cùng nhau tạo dáng và chụp 5 bức ảnh đôi sáng tạo",
      "Mô tả chi tiết về trang phục bạn thấy người kia quyến rũ nhất",
      "Xem một video hài hước cùng nhau và ôm nhau trong lúc xem"
    ],
    hot: [
      "Cởi một món đồ của mình một cách quyến rũ",
      "Hôn và cắn nhẹ tai người kia trong 1 phút",
      "Bịt mắt người kia và hôn khắp người họ trong 2 phút",
      "Tự vuốt ve cơ thể mình trong khi người kia xem",
      "Dùng lưỡi liếm từ cổ xuống ngực người kia",
      "Kể chi tiết điều bạn muốn làm với người kia sau buổi chơi game",
      "Massage chân người kia rồi hôn từng ngón chân",
      "Khám phá cơ thể người kia chỉ bằng môi trong 3 phút",
      "Cởi đồ người kia chỉ bằng răng",
      "Thực hiện một điệu nhảy sexy và cởi một món đồ",
      "Mô tả chi tiết điều bạn thấy hấp dẫn nhất về người kia",
      "Làm người kia cảm thấy kích thích mà không chạm vào vùng nhạy cảm",
      "Tự vuốt ve cơ thể mình trong khi kể về ảo tưởng tình dục của bạn",
      "Hôn người kia từ cổ xuống bụng",
      "Để người kia cắn nhẹ khắp cơ thể bạn",
      // Thêm 20 yêu cầu mới cho mức độ nóng bỏng
      "Thực hiện màn thoát y chậm trong 2 phút với nhạc nền gợi cảm",
      "Tự mát-xa ngực của mình trong khi nhìn vào mắt người kia",
      "Dùng lưỡi vẽ một đường từ tai xuống cổ rồi xuống xương quai xanh của người kia",
      "Cởi đồ của người kia đến khi chỉ còn đồ lót trong 1 phút",
      "Hôn và liếm vành tai người kia trong khi thì thầm những lời khiêu khích",
      "Mô tả chi tiết bộ phận cơ thể của người kia khiến bạn bị kích thích nhất",
      "Tự vuốt ve vùng nhạy cảm của mình qua lớp quần áo trong 1 phút",
      "Để người kia dùng lưỡi liếm bất kỳ đâu trên cơ thể bạn trong 2 phút",
      "Dùng đá lạnh vuốt ve từ cổ xuống ngực người kia",
      "Hôn sâu kèm với vuốt ve cơ thể người kia trong 2 phút",
      "Để người kia cởi một món đồ của bạn chỉ bằng miệng",
      "Nhảy múa gợi cảm trên đùi người kia trong 1 phút",
      "Mô tả chi tiết về một ảo tưởng tình dục mà bạn muốn thực hiện với người kia",
      "Dùng lụa hoặc khăn che mắt người kia và hôn khắp cơ thể họ",
      "Tự vuốt ve cơ thể mình và mô tả cảm giác đang có",
      "Hôn và cắn nhẹ vùng bụng dưới của người kia",
      "Dùng dầu massage cho người kia một bài mát-xa toàn thân gợi cảm",
      "Để người kia dùng tay chạm khắp cơ thể bạn trong 2 phút",
      "Tạo dáng gợi cảm nhất có thể và giữ trong 30 giây",
      "Dùng lưỡi liếm từng ngón tay của người kia một cách gợi cảm"
    ],
    extreme: [
      "Cởi hết đồ và để người kia ngắm nhìn trong 1 phút",
      "Làm người kia thỏa mãn bằng tay trong 3 phút",
      "Dùng miệng khám phá bộ phận nhạy cảm của người kia",
      "Cho người kia làm bất cứ điều gì họ muốn trong 5 phút",
      "Tự làm mình thỏa mãn trong khi người kia xem",
      "Mô tả chi tiết giấc mơ 18+ gần đây nhất về người kia",
      "Thực hiện tư thế 69 trong 2 phút",
      "Cởi đồ và nhảy lap dance cho người kia",
      "Để người kia trói tay bạn và làm điều họ muốn",
      "Khám phá toàn bộ cơ thể người kia bằng lưỡi",
      "Mô phỏng quan hệ tình dục với người kia trong 2 phút",
      "Để người kia 'tra tấn' khoái cảm cho bạn trong 3 phút",
      "Cởi đồ và tạo dáng theo yêu cầu của người kia",
      "Dùng miệng làm người kia thỏa mãn đến khi họ yêu cầu dừng lại",
      "Thực hiện một hành động táo bạo mà người kia đã luôn muốn thử",
      // Thêm 20 yêu cầu mới cho mức độ cực nóng
      "Cởi hết đồ và để người kia phủ chocolate lên người bạn rồi liếm sạch",
      "Thực hiện quan hệ bằng miệng cho người kia đến khi họ đạt cực khoái",
      "Thực hiện một tư thế quan hệ mới mà hai người chưa từng thử",
      "Để người kia trói bạn vào giường và làm bất cứ điều gì họ muốn trong 10 phút",
      "Tự làm mình đạt cực khoái trước mặt người kia mà không được phép chạm vào bạn",
      "Chụp 5 bức ảnh khỏa thân gợi cảm theo tư thế đối phương chỉ định",
      "Thực hiện vai diễn người phục vụ khỏa thân cho người kia trong 10 phút",
      "Để người kia đeo bịt mắt cho bạn và dùng đồ chơi người lớn trên cơ thể bạn",
      "Thực hiện quan hệ ở một vị trí công cộng nhưng kín đáo (ban công, cửa sổ...)",
      "Để người kia 'phạt' bạn bằng một dụng cụ SM nhẹ nhàng (roi da, còng tay...)",
      "Thử nghiệm vai diễn chủ-tớ trong 15 phút, với người kia làm chủ nhân",
      "Thực hiện quan hệ trước gương để cả hai cùng xem",
      "Thử nghiệm edging - đưa người kia đến gần cực khoái nhiều lần nhưng không cho họ đạt được",
      "Mặc trang phục cosplay gợi cảm theo sở thích của người kia",
      "Thực hiện quan hệ tình dục bằng miệng đồng thời cho người kia",
      "Để người kia 'sử dụng' bạn theo bất kỳ cách nào họ muốn trong 15 phút",
      "Thử nghiệm một loại đồ chơi tình dục mới mà hai người chưa dùng bao giờ",
      "Thực hiện các tư thế Kama Sutra nâng cao mà hai người chưa từng thử",
      "Để người kia thực hiện một ảo tưởng tình dục đặc biệt của họ với bạn",
      "Tự đạo diễn một màn trình diễn thoát y hoàn toàn, kết thúc bằng quan hệ tình dục"
    ]
  };

  const rollDice = () => {
    if (rolling || !betType) return;
    setRolling(true);
    setChallenge(null);
    setResult(null);
    
    // Play dice rolling sound
    playSoundIfEnabled('diceRoll');
    
    const rollInterval = setInterval(() => {
      setDice1(Math.floor(Math.random() * 6) + 1);
      setDice2(Math.floor(Math.random() * 6) + 1);
    }, 50);

    setTimeout(() => {
      clearInterval(rollInterval);
      const finalDice1 = Math.floor(Math.random() * 6) + 1;
      const finalDice2 = Math.floor(Math.random() * 6) + 1;
      setDice1(finalDice1);
      setDice2(finalDice2);

      const sum = finalDice1 + finalDice2;

      // Determine win/lose based on bet type
      // Tài (Big): Sum is 8-12
      // Xỉu (Small): Sum is 3-7
      const didWin = (betType === 'tai' && sum >= 8 && sum <= 12) || (betType === 'xiu' && sum >= 3 && sum <= 7);

      setResult(didWin ? 'win' : 'lose');
      
      // Play win/lose sound
      playSoundIfEnabled(didWin ? 'success' : 'fail');

      // If lost, get a challenge
      if (!didWin) {
        const challengeIndex = (sum - 2) % challenges[gameLevel].length;
        setChallenge(challenges[gameLevel][challengeIndex]);
      }

      setRolling(false);

      // Reset bet type after each roll so user has to choose again
      setTimeout(() => {
        setBetType(null);
      }, 3000);
    }, 2000);
  };

  const getDiceColor = () => {
    switch(gameLevel) {
      case 'mild': return 'from-blue-400 to-green-500';
      case 'hot': return 'from-orange-400 to-red-500';
      case 'extreme': return 'from-purple-500 to-pink-600';
      default: return 'from-blue-400 to-green-500';
    }
  };

  const getLevelBadge = () => {
    switch(gameLevel) {
      case 'mild': return { text: 'Nhẹ Nhàng', color: 'bg-green-500', emoji: '😊' };
      case 'hot': return { text: 'Nóng Bỏng', color: 'bg-red-500', emoji: '🔥' };
      case 'extreme': return { text: 'Cực Dâm', color: 'bg-purple-600', emoji: '💦' };
      default: return { text: 'Nhẹ Nhàng', color: 'bg-green-500', emoji: '😊' };
    }
  };

  const selectLevel = (level) => {
    playSoundIfEnabled('click');
    setGameLevel(level);
    setShowLevelSelector(false);
  };

  if (showPrivacy) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            setShowPrivacy(false);
          }}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Điều Khoản & Quyền Riêng Tư
            </h1>

            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">1. Giới Hạn Độ Tuổi:</h2>
                <p className="text-pink-200">
                  Ứng dụng này chỉ dành cho người từ 18 tuổi trở lên. Bằng việc sử dụng ứng dụng này, bạn xác nhận mình đã đủ tuổi theo quy định.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">2. Quyền Sở Hữu:</h2>
                <p className="text-pink-200">
                  Game này được tạo và sở hữu độc quyền bởi Lux Homestay. Mọi hình thức sao chép, phân phối lại mà không có sự cho phép đều bị nghiêm cấm.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">3. Nội Dung:</h2>
                <p className="text-pink-200">
                  Trò chơi có chứa nội dung người lớn và chỉ nên chơi trong không gian riêng tư. Người dùng tự chịu trách nhiệm về việc sử dụng trò chơi này.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">4. Quyền Riêng Tư:</h2>
                <p className="text-pink-200">
                  Chúng tôi không thu thập, lưu trữ hay chia sẻ bất kỳ thông tin cá nhân nào của người dùng. Trò chơi được chơi hoàn toàn ngoại tuyến.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">5. Miễn Trừ Trách Nhiệm:</h2>
                <p className="text-pink-200">
                  Lux Homestay không chịu trách nhiệm về bất kỳ hậu quả nào phát sinh từ việc sử dụng trò chơi này. Người dùng tự chịu trách nhiệm về quyết định và hành động của mình.
                </p>
              </div>

              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  playSoundIfEnabled('click');
                  setShowPrivacy(false);
                }}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu & Đồng Ý
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showHowToPlay) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            setShowHowToPlay(false);
          }}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              🎲 Hướng Dẫn Chơi 🎲
            </h1>

            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">Luật chơi:</h2>
                <p className="text-pink-200">
                  Đây là trò chơi xúc xắc Tài Xỉu phiên bản tình yêu. Mỗi lượt chơi, bạn sẽ đặt cược Tài hoặc Xỉu trước khi tung xúc xắc.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">Cách đặt cược:</h2>
                <ul className="list-disc list-inside space-y-2 text-pink-200">
                  <li><strong className="text-white">Tài:</strong> Tổng điểm 2 xúc xắc từ 8 đến 12</li>
                  <li><strong className="text-white">Xỉu:</strong> Tổng điểm 2 xúc xắc từ 3 đến 7</li>
                </ul>
                <p className="text-pink-200 mt-2">
                  <strong className="text-white">Lưu ý:</strong> Sau mỗi lần tung xúc xắc, bạn cần phải đặt cược lại cho lượt tiếp theo.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">Kết quả:</h2>
                <ul className="list-disc list-inside space-y-2 text-pink-200">
                  <li><strong className="text-white">Thắng:</strong> Bạn đoán đúng! Không có thử thách.</li>
                  <li><strong className="text-white">Thua:</strong> Bạn đoán sai và phải thực hiện một thử thách ngẫu nhiên dựa trên mức độ bạn đã chọn.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">Mức độ thử thách:</h2>
                <ul className="list-disc list-inside space-y-2 text-pink-200">
                  <li><strong className="text-white">Nhẹ Nhàng 😊:</strong> Thử thách lãng mạn, ngọt ngào</li>
                  <li><strong className="text-white">Nóng Bỏng 🔥:</strong> Thử thách gợi cảm, quyến rũ</li>
                  <li><strong className="text-white">Cực Dâm 💦:</strong> Thử thách cực kỳ táo bạo và dâm đãng</li>
                </ul>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  playSoundIfEnabled('click');
                  setShowHowToPlay(false);
                }}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu!
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showLevelSelector) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            navigate('/');
          }}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-4xl font-bold text-white">
                🎲 Xúc Xắc Tình Yêu 🎲
              </h1>
              
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    playSoundIfEnabled('click');
                    setShowHowToPlay(true);
                  }}
                  className="bg-white/20 p-2 rounded-full hover:bg-white/30 transition-all"
                >
                  <SafeIcon icon={FiHelpCircle} className="text-white text-xl" />
                </motion.button>
                
                <SoundToggle />
              </div>
            </div>

            <p className="text-pink-200 text-center mb-8">
              Chọn mức độ thử thách mà bạn muốn thực hiện:
            </p>

            <div className="space-y-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => selectLevel('mild')}
                className="w-full bg-gradient-to-r from-green-400 to-blue-500 hover:from-green-500 hover:to-blue-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300 flex items-center"
              >
                <div className="bg-white/20 p-3 rounded-full mr-4">😊</div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Nhẹ Nhàng</h3>
                  <p className="text-sm opacity-90">Những thử thách ngọt ngào và lãng mạn</p>
                </div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => selectLevel('hot')}
                className="w-full bg-gradient-to-r from-orange-400 to-red-500 hover:from-orange-500 hover:to-red-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300 flex items-center"
              >
                <div className="bg-white/20 p-3 rounded-full mr-4">🔥</div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Nóng Bỏng</h3>
                  <p className="text-sm opacity-90">Thử thách gợi cảm và quyến rũ</p>
                </div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => selectLevel('extreme')}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300 flex items-center"
              >
                <div className="bg-white/20 p-3 rounded-full mr-4">💦</div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Cực Dâm</h3>
                  <p className="text-sm opacity-90">Thử thách cực kỳ nóng bỏng và dâm đãng</p>
                </div>
              </motion.button>
            </div>

            <div className="mt-8 text-center">
              <p className="text-pink-200 text-xs">⚠️ Chỉ dành cho người trên 18 tuổi</p>
              <p className="text-pink-200 text-xs mb-4">Đảm bảo cả hai đều thoải mái với mức độ thử thách</p>

              <button 
                onClick={() => {
                  playSoundIfEnabled('click');
                  setShowPrivacy(true);
                }}
                className="text-pink-300 text-xs underline"
              >
                Điều khoản & Quyền riêng tư
              </button>

              <p className="text-pink-200 text-xs mt-4">
                © 2023 Lux Homestay. Bảo lưu mọi quyền.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            navigate('/');
          }}
          className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        
        <SoundToggle className="bg-white/20" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="flex items-center justify-center gap-2 mb-1">
            <h1 className="text-3xl font-bold text-white">
              🎲 Xúc Xắc Tình Yêu 🎲
            </h1>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                playSoundIfEnabled('click');
                setShowHowToPlay(true);
              }}
              className="bg-white/20 p-1 rounded-full hover:bg-white/30 transition-all ml-1"
            >
              <SafeIcon icon={FiInfo} className="text-white text-sm" />
            </motion.button>
          </div>

          <div className="flex items-center justify-center gap-2">
            <div className={`${getLevelBadge().color} text-white text-xs font-bold px-3 py-1 rounded-full inline-block`}>
              {getLevelBadge().emoji} {getLevelBadge().text}
            </div>
            <button 
              onClick={() => {
                playSoundIfEnabled('click');
                setShowLevelSelector(true);
              }}
              className="text-xs text-pink-300 underline"
            >
              Đổi
            </button>
          </div>
        </motion.div>

        <div className="max-w-md w-full">
          {!rolling && !betType && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white/10 backdrop-blur-lg p-6 rounded-3xl mb-8"
            >
              <h3 className="text-xl text-white font-bold text-center mb-4">Đặt cược:</h3>
              <div className="flex justify-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    playSoundIfEnabled('click');
                    setBetType('tai');
                  }}
                  className={`px-6 py-3 rounded-xl text-white font-bold ${
                    betType === 'tai' ? 'bg-blue-600' : 'bg-white/20'
                  } transition-all`}
                >
                  TÀI (8-12)
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    playSoundIfEnabled('click');
                    setBetType('xiu');
                  }}
                  className={`px-6 py-3 rounded-xl text-white font-bold ${
                    betType === 'xiu' ? 'bg-red-600' : 'bg-white/20'
                  } transition-all`}
                >
                  XỈU (3-7)
                </motion.button>
              </div>
            </motion.div>
          )}

          {betType && !rolling && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-white mb-6"
            >
              <p className="bg-white/10 backdrop-blur-lg p-3 rounded-xl inline-block">
                Bạn đã chọn: <span className="font-bold">{betType === 'tai' ? 'TÀI (8-12)' : 'XỈU (3-7)'}</span>
              </p>
            </motion.div>
          )}

          <div className="flex justify-center gap-8 mb-8">
            <motion.div
              animate={{
                rotate: rolling ? [0, 360, 720, 1080] : 0,
                scale: rolling ? [1, 1.2, 1] : 1,
              }}
              transition={{ duration: 2 }}
              className={`bg-gradient-to-br ${getDiceColor()} w-24 h-24 rounded-xl flex items-center justify-center text-4xl font-bold text-white shadow-lg`}
            >
              {dice1}
            </motion.div>
            <motion.div
              animate={{
                rotate: rolling ? [0, -360, -720, -1080] : 0,
                scale: rolling ? [1, 1.2, 1] : 1,
              }}
              transition={{ duration: 2 }}
              className={`bg-gradient-to-br ${getDiceColor()} w-24 h-24 rounded-xl flex items-center justify-center text-4xl font-bold text-white shadow-lg`}
            >
              {dice2}
            </motion.div>
          </div>

          <AnimatePresence mode="wait">
            {result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`p-6 rounded-3xl shadow-2xl text-white relative overflow-hidden mb-8 ${
                  result === 'win' ? 'bg-gradient-to-br from-green-500 to-blue-500' : 'bg-gradient-to-br from-red-500 to-pink-500'
                }`}
              >
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-2">
                    {result === 'win' ? '🎉 Bạn Thắng! 🎉' : '😈 Bạn Thua! 😈'}
                  </h3>
                  <p className="text-lg">
                    Tổng điểm: <span className="font-bold">{dice1 + dice2}</span> {' '}
                    ({dice1 + dice2 >= 8 ? 'TÀI' : 'XỈU'})
                  </p>

                  {result === 'win' ? (
                    <p className="mt-2">Chúc mừng! Không có thử thách lần này.</p>
                  ) : challenge ? (
                    <div className="mt-4 p-4 bg-white/20 rounded-xl">
                      <h4 className="font-bold mb-2">Thử Thách:</h4>
                      <p className="text-xl">{challenge}</p>
                    </div>
                  ) : null}

                  {!betType && (
                    <p className="mt-4 text-sm bg-white/20 p-2 rounded-lg">
                      Chọn TÀI hoặc XỈU để tung xúc xắc tiếp!
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={rollDice}
            disabled={rolling || !betType}
            className={`
              w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-bold py-4 px-8 rounded-2xl shadow-lg flex items-center justify-center gap-3 text-lg transition-all duration-300
            `}
          >
            <SafeIcon icon={FiDice} className="text-xl" />
            {!betType ? "Hãy Đặt Cược Trước" : rolling ? "Đang Tung..." : "Tung Xúc Xắc"}
          </motion.button>

          <div className="flex items-center justify-between mt-6">
            <button 
              onClick={() => {
                playSoundIfEnabled('click');
                setShowPrivacy(true);
              }}
              className="text-pink-200 text-xs underline"
            >
              Điều khoản & Quyền riêng tư
            </button>
            <p className="text-pink-200 text-xs">
              © 2023 Lux Homestay
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiceGame;